import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Button,
  Checkbox,
  LoadingButton,
  SelectionControlLabel,
  TextField,
  Typography,
} from '@one-thd/sui-atomic-components';
import { TextToConfirm } from '@one-thd/sui-icons';
import { useConfigService } from '@thd-nucleus/experience-context';
import { CustomerMultifactorAuthentication } from '@thd-olt-component-react/customer-multifactor-authentication';
import {
  MfaProvider,
  useMfaContext,
} from '@thd-olt-component-react/mfa-passkey-drawer';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import {
  Controller,
  FieldError,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {
  MAX_T2C_TENDER_LIMIT_REACHED_CODE,
  MFA_ERR_000,
  T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT,
} from '../../../constants/error-codes';
import {
  UnenrollT2CConfirmModalBodyText,
  UnenrollT2CConfirmModalHeaderText,
} from '../../../constants/pro-house-account';
import {
  ERR_GENERIC,
  ERR_MAX_T2C_TENDER_LIMIT,
} from '../../../constants/response-error-messages';
import { useCustomerInfo } from '../../../hooks/use-customerInfo';
import { usePayments } from '../../../hooks/use-payments';
import { usePhaUnenrollT2cConfirmModal } from '../../../hooks/use-pha-unenroll-t2c-confirm-modal';
import {
  TApprovers,
  TProHouseAccountAction,
} from '../../../models/b2b/payments';
import {
  IAlertMessage,
  TAlertStatus,
} from '../../../models/common/alert-messages';
import { IPaymentCardCommonData } from '../../../models/common/payments';
import { TPaymentCardErrorResponse } from '../../../models/common/credit-cards';
import { getPaymentsErrorCode } from '../../../utils/errors';
import {
  extractPhoneNumbersFromErrorMessage,
  getDuplicateT2cErrorMessages,
  removeNonDigitsFromString,
} from '../../../utils/general';
import { getHyphenatedPhoneNumber } from '../../../utils/masking-field';
import {
  ptcRemotePurchasesPtcPostPageErrorAnalytics,
  ptcRemotePurchasesCompletionAdvisoryAnalytics,
} from '../../../utils/payments-analytics';
import {
  trackMfaFlowInitiated,
  trackMfaFlowSuccess,
  trackMfaFlowFailure,
  trackRemotePurchasesAuthStart,
  trackRemotePurchasesAuthSuccess,
  trackRemotePurchasesAuthFailure,
  trackT2cEnrollmentInitiated,
  trackT2cEnrollmentSuccess,
  trackT2cEnrollmentFailure,
} from '../../../utils/passkey-mfa-tracking';
import { getT2CSuccessMessage } from '../../../utils/pro-house-account';
import { onTooltipClick } from '../../../utils/remote-purchases';
import { FormTitle } from '../../shared/form-title/form-title';
import { InfoPopover } from '../../shared/popover/popover';
import { MultipleApproversSection } from '../remote-purchases-auth/multiple-approvers-section';
import { T2CTermsAndConditions } from '../remote-purchases-auth/t2c-terms-conditons';
import { ProHouseAccountInfo } from './pro-house-account-info/pro-house-account-info';
import { validationSchema } from './validation';

export type TProHouseAccountsAuthForm = {
  t2cCheckbox: boolean;
  phoneNumber: string;
  additionalApprovers: TApprovers;
  hasMultipleApprovers: boolean;
};

export interface IProHouseAccountAuthFormProps {
  onCloseForm: () => void;
  onSuccess: (
    action: TProHouseAccountAction,
    message: React.ReactChild,
  ) => void;
  onFailure: (
    action: TProHouseAccountAction,
    message: React.ReactChild,
  ) => void;
  card: IPaymentCardCommonData;
}

const getTextFieldStatus = (error: FieldError | undefined) =>
  error ? 'error' : undefined;

export const ProHouseAccountAuthFormContent = ({
  onCloseForm,
  onSuccess,
  onFailure,
  card,
}: IProHouseAccountAuthFormProps) => {
  const [formAlertMessage, setFormAlertMessage] =
    useState<IAlertMessage | null>(null);
  const [t2cDuplicateAlerts, setT2cDuplicateAlerts] = useState<IAlertMessage[]>(
    [],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] =
    useState<TProHouseAccountsAuthForm | null>(null);
  const [dynamicEncodedAction, setDynamicEncodedAction] = useState<
    string | null
  >(null);
  const isMfaModalEnabled = !!useConfigService('PTC_MFA_MODAL_ENABLED');
  const isMfaDrawerEnabled = !!useConfigService('PTC_MFA_DRAWER_ENABLED');
  const { triggerFlow } = useMfaContext();
  const { enrollPHAT2C, unenrollPHAT2C, t2cMultipleApproverContacts } =
    usePayments();
  const { svocID } = useCustomerInfo();

  const [openConfirmModal] = usePhaUnenrollT2cConfirmModal({
    selectedCard: card,
  });
  const { t2cNumber, paymentId } = card;
  const t2cStatus = card.t2cStatus?.toLowerCase();
  const t2cPending = t2cStatus === 'pending';
  const t2cEnrolled = t2cStatus === 'enrolled';
  const isT2CCurrentlyActive = t2cPending || t2cEnrolled;
  const activeT2COnFile = card.activeT2COnFile;
  const defaultT2CNumber = t2cNumber || activeT2COnFile || '';

  const multipleApproversAllowedSvocIDs = useConfigService(
    'T2C_MULTIPLE_APPROVERS_ALLOWED_SVOCIDS',
  );
  const isSvocIDAllowedForMultipleAprovers =
    typeof multipleApproversAllowedSvocIDs === 'string' &&
    multipleApproversAllowedSvocIDs.includes(svocID);

  const t2cMultipleApproversEnabled =
    !!useConfigService('T2C_MULTIPLE_APPROVERS_ENABLED') ||
    isSvocIDAllowedForMultipleAprovers;
  const isProTradeCreditT2CEnabled = !useConfigService('DISABLE_PTC_T2C');
  const hasMultipleApproversOnFile = !!(
    t2cMultipleApproverContacts && t2cMultipleApproverContacts.length > 0
  );

  const defaultValues: TProHouseAccountsAuthForm = {
    t2cCheckbox: isT2CCurrentlyActive,
    phoneNumber: getHyphenatedPhoneNumber(defaultT2CNumber),
    additionalApprovers: hasMultipleApproversOnFile
      ? t2cMultipleApproverContacts.map(
          ({ fullName, t2cNumber: number, status }) => ({
            fullName: fullName || '',
            t2cNumber: getHyphenatedPhoneNumber(number),
            status,
          }),
        )
      : [
          {
            fullName: '',
            t2cNumber: getHyphenatedPhoneNumber(defaultT2CNumber),
            status: null,
          },
        ],
    hasMultipleApprovers: t2cMultipleApproversEnabled,
  };

  const formMethods = useForm<TProHouseAccountsAuthForm>({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors: formErrors, isDirty },
  } = formMethods;

  const watchT2cCheckbox = watch('t2cCheckbox');
  const shouldShowMultipleApprovers = watch('hasMultipleApprovers');

  const handleSuccess = (action: TProHouseAccountAction) => {
    const text = getT2CSuccessMessage(action);
    onSuccess(action, text);
    ptcRemotePurchasesCompletionAdvisoryAnalytics(text);
  };

  const handleFailure = (
    action: TProHouseAccountAction,
    optionalMessage?: React.ReactChild,
  ) => {
    onFailure(action, optionalMessage || ERR_GENERIC.message);
  };

  const resetMfaState = (action: string) => {
    setShowMfaModal(false);
    if (action === 'success') {
      proceedWithSubmit(pendingFormValues as TProHouseAccountsAuthForm);
    }
    if (action === 'close' || action === 'failure') {
      setIsSubmitting(false);
    }
    setPendingFormValues(null);
    setDynamicEncodedAction(null);
  };

  const handleMfaRequired = (
    error: AxiosError<TPaymentCardErrorResponse>,
    values: TProHouseAccountsAuthForm,
  ): boolean => {
    const mfaEncodedAction =
      error.response?.data?.paymentCards?.errors?.[0]?.encodedAction;
    if (!mfaEncodedAction) return false;

    setDynamicEncodedAction(mfaEncodedAction);
    setPendingFormValues(values);
    if (isMfaDrawerEnabled) {
      trackMfaFlowInitiated('passkey-drawer', {
        action: 'T2C_ENROLLMENT',
      });
      triggerFlow(
        mfaEncodedAction,
        () => {
          trackMfaFlowSuccess('passkey-drawer', {
            action: 'T2C_ENROLLMENT',
          });
          proceedWithSubmit(values);
          setPendingFormValues(null);
          setDynamicEncodedAction(null);
        },
        () => {
          trackMfaFlowFailure('passkey-drawer', {
            action: 'T2C_ENROLLMENT',
            errorCode: 'USER_CANCELLED_MFA',
          });
          setIsSubmitting(false);
          setPendingFormValues(null);
          setDynamicEncodedAction(null);
        },
      );
    } else if (isMfaModalEnabled) {
      setShowMfaModal(true);
    } else {
      return false;
    }
    return true;
  };

  const handleT2CApiError = (
    error: AxiosError<TPaymentCardErrorResponse>,
    action: TProHouseAccountAction,
    values: TProHouseAccountsAuthForm,
  ) => {
    if (!error) {
      handleFailure(action);
      return;
    }
    const status = error.response?.status;

    // 401 — session timeout; interceptor handles redirect to sign-in
    if (status === 401) {
      return;
    }

    // 403 — check for MFA_ERR_000 and extract encodedAction
    if (status === 403) {
      const errorCode = getPaymentsErrorCode(error);
      if (errorCode === MFA_ERR_000) {
        if (handleMfaRequired(error, values)) return;
      }
      handleFailure(action);
      return;
    }

    // Enroll-specific error codes (check before generic 400 handler)
    if (action === TProHouseAccountAction.ENROLL) {
      const errorCode = getPaymentsErrorCode(error);

      if (errorCode === MAX_T2C_TENDER_LIMIT_REACHED_CODE) {
        ptcRemotePurchasesPtcPostPageErrorAnalytics(
          ERR_MAX_T2C_TENDER_LIMIT.message,
        );
        handleFailure(action, ERR_MAX_T2C_TENDER_LIMIT.message);
        return;
      }

      if (errorCode === T2C_NUMBER_ASSOCIATED_ANOTHER_ACCOUNT) {
        const errorMessage =
          error.response?.data?.paymentCards?.errors?.[0]?.errorMessage ||
          error.response?.data?.errors?.[0]?.errorMessage;
        const phoneNumbers = errorMessage
          ? extractPhoneNumbersFromErrorMessage(errorMessage)
          : [];

        const errorMessages = getDuplicateT2cErrorMessages(phoneNumbers);
        ptcRemotePurchasesPtcPostPageErrorAnalytics(errorMessages[0]);
        setT2cDuplicateAlerts(
          errorMessages.map((msg) => ({
            alertStatus: TAlertStatus.ERROR,
            text: msg,
          })),
        );
        return;
      }
    }

    handleFailure(action);
  };

  const unenroll = (values: TProHouseAccountsAuthForm) => {
    const { additionalApprovers } = values;
    trackT2cEnrollmentInitiated({
      action: 'UNENROLL',
      cardType: card?.cardType,
      locType: card?.locType,
    });
    return unenrollPHAT2C({
      paymentId,
      t2cApprovers: shouldShowMultipleApprovers ? additionalApprovers : null,
    })
      .then((success: boolean) => {
        if (success) {
          trackT2cEnrollmentSuccess({
            action: 'UNENROLL',
            cardType: card?.cardType,
            locType: card?.locType,
          });
          return handleSuccess(TProHouseAccountAction.UNENROLL);
        }
        trackT2cEnrollmentFailure({
          action: 'UNENROLL',
          errorCode: 'UNENROLL_FAILED',
          cardType: card?.cardType,
          locType: card?.locType,
        });
        return handleFailure(TProHouseAccountAction.UNENROLL);
      })
      .catch((error: AxiosError<TPaymentCardErrorResponse>) => {
        trackT2cEnrollmentFailure({
          errorCode: (error && getPaymentsErrorCode(error)) || 'UNKNOWN',
          httpStatus: error?.response?.status,
          action: 'UNENROLL',
          cardType: card?.cardType,
          locType: card?.locType,
        });
        handleT2CApiError(error, TProHouseAccountAction.UNENROLL, values);
      });
  };

  const enroll = (values: TProHouseAccountsAuthForm) => {
    const { phoneNumber, additionalApprovers } = values;
    trackRemotePurchasesAuthStart({
      cardType: card?.cardType,
      locType: card?.locType,
      approversCount: shouldShowMultipleApprovers
        ? additionalApprovers?.length
        : 1,
    });
    trackT2cEnrollmentInitiated({
      action: 'ENROLL',
      cardType: card?.cardType,
      locType: card?.locType,
    });
    return enrollPHAT2C({
      t2cNumber: removeNonDigitsFromString(phoneNumber),
      paymentId,
      locType: card?.locType,
      isResend: false,
      t2cApprovers: shouldShowMultipleApprovers ? additionalApprovers : null,
    })
      .then((success: boolean) => {
        if (success) {
          trackRemotePurchasesAuthSuccess({
            cardType: card?.cardType,
            locType: card?.locType,
            approversCount: shouldShowMultipleApprovers
              ? additionalApprovers?.length
              : 1,
          });
          trackT2cEnrollmentSuccess({
            action: 'ENROLL',
            cardType: card?.cardType,
            locType: card?.locType,
          });
          return handleSuccess(TProHouseAccountAction.ENROLL);
        }
        trackRemotePurchasesAuthFailure({
          cardType: card?.cardType,
          locType: card?.locType,
          approversCount: shouldShowMultipleApprovers
            ? additionalApprovers?.length
            : 1,
          errorCode: 'ENROLL_FAILED',
        });
        trackT2cEnrollmentFailure({
          action: 'ENROLL',
          errorCode: 'ENROLL_FAILED',
          cardType: card?.cardType,
          locType: card?.locType,
        });
        return handleFailure(TProHouseAccountAction.ENROLL);
      })
      .catch((error: AxiosError<TPaymentCardErrorResponse>) => {
        const errorCode = (error && getPaymentsErrorCode(error)) || 'UNKNOWN';
        trackRemotePurchasesAuthFailure({
          cardType: card?.cardType,
          locType: card?.locType,
          approversCount: shouldShowMultipleApprovers
            ? additionalApprovers?.length
            : 1,
          errorCode,
          httpStatus: error?.response?.status,
        });
        trackT2cEnrollmentFailure({
          errorCode,
          httpStatus: error?.response?.status,
          action: 'ENROLL',
          cardType: card?.cardType,
          locType: card?.locType,
        });
        handleT2CApiError(error, TProHouseAccountAction.ENROLL, values);
      });
  };

  const proceedWithSubmit = (values: TProHouseAccountsAuthForm) => {
    setIsSubmitting(true);

    // Did they uncheck the box?
    if (!values.t2cCheckbox) {
      const confirmModalProps = {
        bodyText: UnenrollT2CConfirmModalBodyText,
        headerText: UnenrollT2CConfirmModalHeaderText,
      };
      openConfirmModal(confirmModalProps)
        .then((confirmed: boolean) => {
          if (confirmed) {
            return unenroll(values);
          }
          onCloseForm();
          return Promise.resolve();
        })
        .finally(() => {
          setIsSubmitting(false);
        });
      return;
    }

    enroll(values).finally(() => {
      setIsSubmitting(false);
    });
  };

  const onSubmitForm: SubmitHandler<TProHouseAccountsAuthForm> = (
    values: TProHouseAccountsAuthForm,
  ) => {
    setFormAlertMessage(null);
    setT2cDuplicateAlerts([]);
    if (!isDirty) {
      onCloseForm();
      return;
    }

    proceedWithSubmit(values);
  };

  return (
    <div className="sm:sui-max-w-[485px] sui-flex sui-flex-col sui-gap-10">
      <FormTitle
        title="Authorize pro trade credit for Remote Purchases"
        actionLabel="Cancel"
        onActionClick={() => onCloseForm()}
      />
      <Typography>
        Let your team members check out at The Home Depot using Pro Trade
        Credit. Approve and verify in-store purchases via text message.
      </Typography>
      <ProHouseAccountInfo proHouseAccount={card} />
      <div>
        {!isProTradeCreditT2CEnabled && (
          <Alert
            status="error"
            headline="Self-Service Access Disabled for Pro Trade Credit"
          >
            Enrollment and updates for Remote Purchases require assistance from
            a sales representative. Contact your sales representative or call
            1-866-333-3551 to get started.
          </Alert>
        )}
        {isProTradeCreditT2CEnabled && (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmitForm)}>
              <div className="sui-flex sui-gap-4">
                <div className="sui-mt-2 sui-w-6 sui-h-6">
                  <TextToConfirm />
                </div>
                <div>
                  <SelectionControlLabel
                    label={
                      <div className="sui-flex sui-items-center">
                        <Typography weight="bold">Text2Confirm</Typography>
                      </div>
                    }
                  >
                    <Controller
                      name="t2cCheckbox"
                      control={control}
                      render={({
                        field: { value, name, onChange, disabled, onBlur },
                      }) => (
                        <Checkbox
                          name={name}
                          onChange={onChange}
                          disabled={disabled}
                          onBlur={onBlur}
                          checked={value}
                          data-testid="t2c-checkbox"
                          inputAttributes={{ 'aria-label': 'controlled' }}
                        />
                      )}
                    />
                  </SelectionControlLabel>
                  <div className="sui-ml-[-16px] sui-inline-block">
                    <InfoPopover
                      title="Approve by Text2Confirm"
                      iconSize="small"
                      onIconClick={() => onTooltipClick('T2C')}
                    >
                      <div className="sui-w-[260px] sui-text-xs">
                        Receive a text message with checkout details and reply
                        to approve the in-store purchase.
                      </div>
                    </InfoPopover>
                  </div>
                  {watchT2cCheckbox && (
                    <>
                      {shouldShowMultipleApprovers ? (
                        <div
                          className="sui-mt-6"
                          data-testid="pha-multi-approver-section"
                        >
                          <MultipleApproversSection />
                        </div>
                      ) : (
                        <>
                          <div className="sui-my-6">
                            <Typography>
                              We’ll send a text message to the phone number
                              provided below.
                            </Typography>
                          </div>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            render={({
                              field: {
                                ref,
                                name,
                                onChange,
                                value,
                                ...restFields
                              },
                            }) => (
                              <TextField
                                label="Mobile Phone Number*"
                                value={value}
                                name={name}
                                fullWidth
                                helpMessage="This number is used for all cards authorized for Text2Confirm."
                                status={getTextFieldStatus(
                                  formErrors.phoneNumber,
                                )}
                                statusMessage={formErrors.phoneNumber?.message}
                                InputProps={{
                                  inputProps: { 'data-qm-mask': '' },
                                  onChange: (
                                    event: React.ChangeEvent<HTMLInputElement>,
                                  ) => {
                                    onChange(
                                      getHyphenatedPhoneNumber(
                                        event.currentTarget.value || '',
                                      ),
                                    );
                                  },
                                  ...restFields,
                                }}
                              />
                            )}
                          />
                        </>
                      )}

                      <T2CTermsAndConditions paymentType="Pro Trade Credit" />
                    </>
                  )}
                  {formAlertMessage && (
                    <div
                      className="sui-mt-10"
                      data-testid="general-error-message"
                    >
                      <Alert status={formAlertMessage.alertStatus}>
                        {formAlertMessage.text}
                      </Alert>
                    </div>
                  )}
                  {t2cDuplicateAlerts.map((alert, index) => (
                    <div
                      key={index}
                      className="sui-mt-10"
                      data-testid={`t2c-duplicate-alert-${index}`}
                    >
                      <Alert status={alert.alertStatus}>{alert.text}</Alert>
                    </div>
                  ))}
                </div>
              </div>
              <div className="sui-mt-10 sui-grid sui-grid-cols-1 sui-gap-5 sm:sui-grid-cols-2">
                <div className="sui-order-last sm:sui-order-first">
                  <Button fullWidth variant="secondary" onClick={onCloseForm}>
                    Cancel
                  </Button>
                </div>
                <LoadingButton
                  loading={isSubmitting}
                  fullWidth
                  variant="primary"
                  type="submit"
                >
                  Save
                </LoadingButton>
              </div>
            </form>
          </FormProvider>
        )}
      </div>
      {isProTradeCreditT2CEnabled &&
        isMfaModalEnabled &&
        !isMfaDrawerEnabled &&
        showMfaModal &&
        dynamicEncodedAction &&
        pendingFormValues && (
          <CustomerMultifactorAuthentication
            encodedAction={dynamicEncodedAction}
            experienceName="customer-account-address-payments"
            onSuccess={() => {
              resetMfaState('success');
            }}
            onClose={() => resetMfaState('close')}
            onFailure={() => resetMfaState('failure')}
          />
        )}
    </div>
  );
};

export const ProHouseAccountAuthForm = (
  props: IProHouseAccountAuthFormProps,
) => (
  <MfaProvider>
    <ProHouseAccountAuthFormContent {...props} />
  </MfaProvider>
);
